import React, { useEffect, useMemo, useState } from "react";
import { money } from "@/lib/money";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import Stepper from "@/components/Stepper";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, FileCheck, RefreshCw } from "lucide-react";

const STEPS = [
  { key: "select", label: "Select Loan" },
  { key: "calc", label: "Calculator" },
  { key: "profile", label: "Profile" },
  { key: "ekyc", label: "eKYC" },
  { key: "docs", label: "Documents" },
  { key: "bank", label: "Bank Details" },
  { key: "submit", label: "Submit" },
];

export default function ApplicationFlow() {
  const { id } = useParams(); // optional existing application
  const [sp] = useSearchParams();
  const startStep = parseInt(sp.get("step") || "0", 10) || 0;
  const nav = useNavigate();
  const [step, setStep] = useState(startStep);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(null);
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState("");
  const [freq, setFreq] = useState("monthly");
  const [calc, setCalc] = useState(null);
  const [appId, setAppId] = useState(id || null);
  const [app, setApp] = useState(null);
  const [profile, setProfile] = useState({});
  const [ekyc, setEkyc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [bank, setBank] = useState({});
  const [loading, setLoading] = useState(false);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);

  useEffect(() => {
    api.get("/loan-products", { params: { active_only: true } }).then((r) => setProducts(r.data));
    api.get("/users/me/profile").then((r) => setProfile(r.data || {}));
    if (id) loadApp(id);
  }, [id]);

  const loadApp = async (aid) => {
    const { data } = await api.get(`/applications/${aid}`);
    setApp(data);
    setAppId(data.id);
    setProductId(data.product_id);
    setAmount(String(data.amount));
    setDuration(String(data.duration));
    setFreq(data.repayment_frequency);
    setCalc(data.calculator_snapshot);
    setDocs(data.documents || []);
    setEkyc(data.ekyc || null);
    setBank(data.bank_nomination || {});
  };

  const runCalc = async () => {
    if (!productId || !amount || !duration) return;
    try {
      const { data } = await api.post("/calculator", {
        product_id: productId, amount: Number(amount), duration: Number(duration), repayment_frequency: freq,
      });
      setCalc(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (step === 1 && productId && amount && duration) runCalc(); /* eslint-disable-next-line */ }, [step, productId, amount, duration, freq]);

  const createOrUpdateApp = async () => {
    if (appId) return appId;
    const { data } = await api.post("/applications", {
      product_id: productId, amount: Number(amount), duration: Number(duration), repayment_frequency: freq,
    });
    setAppId(data.id);
    setApp(data);
    nav(`/borrower/applications/${data.id}/edit?step=2`, { replace: true });
    return data.id;
  };

  const saveProfile = async () => {
    const { data } = await api.put("/users/me/profile", profile);
    setProfile(data);
    toast.success("Profile saved");
  };

  const startEkyc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/applications/${appId}/ekyc/start`);
      setEkyc(data);
      toast.success("eKYC session started");
    } finally { setLoading(false); }
  };

  const submitEkyc = async (idType, idNumber) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/applications/${appId}/ekyc/submit`, {
        id_type: idType, id_number: idNumber, selfie_captured: true, address_proof: true,
      });
      setEkyc(data);
      toast.success(data.status === "verified" ? "eKYC verified" : `eKYC status: ${data.status}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const uploadDoc = async (doc_type, file) => {
    const fd = new FormData();
    fd.append("doc_type", doc_type);
    fd.append("file", file);
    const { data } = await api.post(`/applications/${appId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setDocs((prev) => {
      const others = prev.filter((d) => d.doc_type !== doc_type);
      return [...others, data];
    });
    toast.success(`${doc_type} uploaded`);
  };

  const saveBank = async () => {
    const { data } = await api.post(`/applications/${appId}/bank`, bank);
    setBank(data);
    toast.success("Bank details saved");
  };

  const submitApplication = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/applications/${appId}/submit`);
      toast.success("Application submitted!");
      nav(`/borrower/applications/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const goNext = async () => {
    try {
      if (step === 0) { if (!productId) return toast.error("Select a loan product"); }
      if (step === 1) {
        if (!amount || !duration) return toast.error("Enter amount and duration");
        await createOrUpdateApp();
      }
      if (step === 2) { await saveProfile(); }
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Apply for a loan</h1>
            <p className="text-slate-600 text-sm mt-1">Complete the 7 steps to submit your application.</p>
          </div>
          {app && <StatusBadge status={app.status}/>}
        </div>

        <Card className="rounded-xl border-slate-200"><CardContent className="p-4">
          <Stepper steps={STEPS} current={step} onStepClick={(i) => { if (appId || i <= 1) setStep(i); }}/>
        </CardContent></Card>

        {step === 0 && (
          <SelectLoan products={products} productId={productId} setProductId={setProductId}/>
        )}
        {step === 1 && (
          <Calculator product={product} amount={amount} setAmount={setAmount} duration={duration} setDuration={setDuration} freq={freq} setFreq={setFreq} calc={calc} onContinue={goNext}/>
        )}
        {step === 2 && (
          <ProfileForm profile={profile} setProfile={setProfile}/>
        )}
        {step === 3 && (
          <EkycPanel ekyc={ekyc} startEkyc={startEkyc} submitEkyc={submitEkyc} loading={loading}/>
        )}
        {step === 4 && product && (
          <DocsPanel product={product} docs={docs} uploadDoc={uploadDoc}/>
        )}
        {step === 5 && (
          <BankPanel bank={bank} setBank={setBank} saveBank={saveBank} profileName={profile.full_legal_name}/>
        )}
        {step === 6 && (
          <SubmitPanel app={app} product={product} calc={calc} docs={docs} ekyc={ekyc} bank={bank} profile={profile} onSubmit={submitApplication} loading={loading}/>
        )}

        <div className="sticky bottom-0 -mx-3 sm:mx-0 px-3 sm:px-0 py-3 sm:py-0 bg-slate-50/95 sm:bg-transparent backdrop-blur sm:backdrop-blur-0 border-t sm:border-t-0 border-slate-200 flex justify-between gap-3">
          <Button variant="outline" data-testid="stepper-back-btn" onClick={() => setStep(Math.max(0, step-1))} disabled={step===0} className="flex-1 sm:flex-none"><ArrowLeft className="h-4 w-4 mr-1"/> Back</Button>
          {step < 6 ? (
            <Button className="bg-blue-900 hover:bg-blue-800 flex-1 sm:flex-none" data-testid="stepper-next-btn" onClick={goNext}>Next <ArrowRight className="h-4 w-4 ml-1"/></Button>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}

function SelectLoan({ products, productId, setProductId }) {
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
      <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Choose a loan product</h2>
      <p className="text-slate-600 text-sm">Pick the one that fits your need.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <button key={p.id}
            data-testid={`select-product-${p.id}`}
            onClick={() => setProductId(p.id)}
            className={`text-left rounded-xl border p-4 transition-all ${productId === p.id ? "border-blue-900 bg-blue-50/40 ring-1 ring-blue-900" : "border-slate-200 hover:border-blue-200 hover:bg-slate-50"}`}>
            <div className="font-semibold text-slate-900">{p.name}</div>
            <div className="text-sm text-slate-600 mt-1">{p.description}</div>
            <div className="text-xs text-slate-500 mt-2">{money(p.min_amount)}–{money(p.max_amount)} · {p.min_duration}–{p.max_duration} {p.duration_unit} · {p.interest_rate}% {p.interest_type}</div>
            {p.representative_apr != null && (
              <div className="mt-2 inline-flex items-baseline gap-1.5 rounded-lg bg-blue-50 border border-blue-100 px-2 py-1">
                <span className="text-[11px] uppercase tracking-wider text-blue-900/70">Representative APR</span>
                <span className="text-sm font-bold text-blue-900 tabular-nums">{Number(p.representative_apr).toFixed(2)}%</span>
              </div>
            )}
          </button>
        ))}
      </div>
    </CardContent></Card>
  );
}

function Calculator({ product, amount, setAmount, duration, setDuration, freq, setFreq, calc, onContinue }) {
  if (!product) {
    return (
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-8 text-slate-500">Select a loan product first.</CardContent>
      </Card>
    );
  }

  const GOLD = "#d6a84f";
  const NAVY = "#061b3a";

  const minAmount = Number(product.min_amount || 500);
  const maxAmount = Number(product.max_amount || 150000);
  const minDuration = Number(product.min_duration || 6);
  const maxDuration = Number(product.max_duration || 60);

  const numericAmount = Math.max(minAmount, Math.min(maxAmount, Number(amount || minAmount)));
  const numericDuration = Math.max(minDuration, Math.min(maxDuration, Number(duration || minDuration)));
  const terms = [6, 12, 24, 36, 48, 60].filter((m) => m >= minDuration && m <= maxDuration);

  const principal = Number(calc?.principal || numericAmount || 0);
  const interest = Number(calc?.interest || 0);
  const serviceFee = Number(calc?.service_fee || 0);
  const processingFee = Number(calc?.processing_fee || 0);
  const totalFees = serviceFee + processingFee;
  const totalPayable = Number(calc?.total_payable || principal + interest + totalFees);
  const perPayment = Number(calc?.per_payment || 0);
  const apr = Number(calc?.effective_apr || product.interest_rate || 0);
  const schedule = calc?.schedule || [];

  const totalForChart = Math.max(totalPayable, 1);
  const principalPct = Math.max(0, Math.min(100, (principal / totalForChart) * 100));
  const interestPct = Math.max(0, Math.min(100, (interest / totalForChart) * 100));
  const feePct = Math.max(0, Math.min(100, (totalFees / totalForChart) * 100));

  const setAmountSafe = (value) => {
    const n = Math.max(minAmount, Math.min(maxAmount, Number(value || minAmount)));
    setAmount(String(n));
  };

  const setDurationSafe = (value) => {
    const n = Math.max(minDuration, Math.min(maxDuration, Number(value || minDuration)));
    setDuration(String(n));
    setFreq("monthly");
  };

  const rows = (schedule.length ? schedule.slice(0, 6) : Array.from({ length: 6 })).map((row, idx) => {
    const payment = Number(row?.amount || perPayment || 0);
    const estInterest = payment * 0.22;
    const estPrincipal = Math.max(payment - estInterest, 0);
    const remaining = Math.max(totalPayable - ((idx + 1) * payment), 0);
    return { idx, payment, estPrincipal, estInterest, remaining };
  });

  return (
    <div className="w-full max-w-[1320px] mx-auto overflow-hidden">
      <div className="mb-5 rounded-2xl bg-[#061b3a] px-5 py-4 text-white shadow-sm flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full border-2 border-[#d6a84f] flex items-center justify-center text-[#f2c766] font-serif text-2xl font-black">M</div>
          <div>
            <div className="font-serif text-2xl tracking-[0.16em] text-[#f2c766]">MABUHAY</div>
            <div className="text-[11px] tracking-[0.36em] text-[#f2c766]">CAPITAL LENDING</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#f2c766]">
          🛡️ Trusted. Reliable. Discreet.
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-5">
        <Card className="rounded-3xl border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-5 sm:p-7">
            <div>
              <h2 className="text-3xl font-black text-[#061b3a]" style={{ fontFamily: "Outfit" }}>Loan Calculator</h2>
              <p className="mt-2 text-slate-600">Estimate your repayments. Adjust the amount and term to see your monthly payment.</p>
            </div>

            <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-[#061b3a]">Loan Amount</div>
                  <div className="mt-3 text-5xl font-black text-[#061b3a]" style={{ fontFamily: "Outfit" }}>
                    ${numericAmount.toLocaleString()}
                  </div>
                </div>

                <div className="w-44">
                  <Input
                    type="number"
                    value={amount}
                    min={minAmount}
                    max={maxAmount}
                    onChange={(e) => setAmountSafe(e.target.value)}
                    className="h-12 text-right font-bold border-slate-300"
                  />
                </div>
              </div>

              <div className="mt-8">
                <input
                  type="range"
                  min={minAmount}
                  max={maxAmount}
                  step="100"
                  value={numericAmount}
                  onChange={(e) => setAmountSafe(e.target.value)}
                  className="w-full accent-[#d6a84f]"
                />
                <div className="mt-2 flex justify-between text-sm font-semibold text-slate-500">
                  <span>${minAmount.toLocaleString()}</span>
                  <span className="text-[#d6a84f]">${numericAmount.toLocaleString()}</span>
                  <span>${maxAmount.toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-8">
                <div className="text-sm font-bold text-[#061b3a]">Loan Term (Months)</div>
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {terms.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDurationSafe(m)}
                      className={`h-12 rounded-xl border text-base font-bold transition ${
                        numericDuration === m
                          ? "bg-[#061b3a] border-[#061b3a] text-[#f2c766] shadow-md"
                          : "bg-white border-slate-300 text-[#061b3a] hover:border-[#d6a84f]"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-600">
                ℹ️ Rates are indicative and subject to approval. APR may vary based on your profile.
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl overflow-hidden shadow-lg border-slate-800 bg-[#061b3a]">
          <CardContent className="p-0">
            <div className="bg-[radial-gradient(circle_at_top_right,#16477f,transparent_35%),linear-gradient(135deg,#061b3a,#020b1d)] p-5 sm:p-7 text-white">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <div className="text-sm font-bold text-white">You Receive <span className="text-[#f2c766]">ⓘ</span></div>
                  <div className="mt-2 text-4xl font-black text-[#f2c766]" style={{ fontFamily: "Outfit" }}>
                    ${principal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">APR <span className="text-[#f2c766]">ⓘ</span></div>
                  <div className="mt-4 text-3xl font-black text-[#f2c766]">{apr}%</div>
                </div>
              </div>

              <div className="my-6 h-px bg-white/25" />

              <div>
                <div className="text-sm font-bold text-white">Monthly Payment <span className="text-[#f2c766]">ⓘ</span></div>
                <div className="mt-2 text-5xl font-black text-white" style={{ fontFamily: "Outfit" }}>
                  ${perPayment ? perPayment.toFixed(2) : "0.00"}
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-white/15 bg-white/5 p-4 space-y-3">
                <SummaryLine label="Principal" value={`$${principal.toFixed(2)}`} />
                <SummaryLine label="Total Interest" value={`$${interest.toFixed(2)}`} />
                <SummaryLine label="Fees" value={`$${totalFees.toFixed(2)}`} />
              </div>

              <div className="mt-6 grid sm:grid-cols-[1fr_0.9fr] gap-5 items-center">
                <div>
                  <div className="text-sm font-bold text-white">Total Payable</div>
                  <div className="mt-2 text-4xl font-black text-[#f2c766]" style={{ fontFamily: "Outfit" }}>
                    ${totalPayable.toFixed(2)}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div
                    className="h-24 w-24 rounded-full shrink-0"
                    style={{
                      background: `conic-gradient(#3b82f6 0 ${principalPct}%, #d6a84f ${principalPct}% ${principalPct + interestPct}%, #e5e7eb ${principalPct + interestPct}% ${principalPct + interestPct + feePct}%, #1e293b ${principalPct + interestPct + feePct}% 100%)`
                    }}
                  />
                  <div className="space-y-2 text-sm">
                    <Legend color="bg-blue-500" label={`Principal (${principalPct.toFixed(1)}%)`} />
                    <Legend color="bg-[#d6a84f]" label={`Interest (${interestPct.toFixed(1)}%)`} />
                    <Legend color="bg-slate-200" label={`Fees (${feePct.toFixed(1)}%)`} />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-5 grid grid-cols-1 xl:grid-cols-[1.45fr_0.65fr] gap-5">
        <Card className="rounded-3xl border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-5 sm:p-6">
            <h3 className="text-2xl font-black text-[#061b3a]" style={{ fontFamily: "Outfit" }}>
              Payment Schedule <span className="font-normal text-slate-600">(Amortization)</span>
            </h3>

            <div className="mt-4 hidden sm:block overflow-x-auto rounded-xl border border-slate-200">
              <div className="min-w-[760px] grid grid-cols-6 bg-[#061b3a] px-4 py-3 text-xs font-black uppercase tracking-widest text-white">
                <div>#</div>
                <div>Payment Date</div>
                <div className="text-right">Payment</div>
                <div className="text-right">Principal</div>
                <div className="text-right">Interest</div>
                <div className="text-right">Remaining Balance</div>
              </div>
              {rows.map((r) => (
                <div key={r.idx} className="min-w-[760px] grid grid-cols-6 px-4 py-3 text-sm border-t border-slate-100 bg-white">
                  <div>{r.idx + 1}</div>
                  <div>Month {r.idx + 1}</div>
                  <div className="text-right">${r.payment.toFixed(2)}</div>
                  <div className="text-right">${r.estPrincipal.toFixed(2)}</div>
                  <div className="text-right">${r.estInterest.toFixed(2)}</div>
                  <div className="text-right font-semibold">${r.remaining.toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 sm:hidden space-y-2">
              {rows.map((r) => (
                <div key={r.idx} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex justify-between font-bold">
                    <span>Month {r.idx + 1}</span>
                    <span className="text-[#061b3a]">${r.payment.toFixed(2)}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-slate-600">
                    <div>Principal<br/><b>${r.estPrincipal.toFixed(2)}</b></div>
                    <div>Interest<br/><b>${r.estInterest.toFixed(2)}</b></div>
                    <div>Balance<br/><b>${r.remaining.toFixed(2)}</b></div>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-4 text-sm text-slate-500">This is a sample computation only and does not guarantee approval. Terms and conditions apply.</p>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-[#d6a84f]/30 bg-[#fffaf0] shadow-sm">
          <CardContent className="p-6 space-y-6">
            <Benefit icon="🛡️" title="Transparent" desc="No hidden charges. Clear and upfront." />
            <Benefit icon="⏱️" title="Fast Approval" desc="Get a decision in as fast as 24 hours." />
            <Benefit icon="🔒" title="Secure & Private" desc="Your data is safe with us." />
            <Benefit icon="👥" title="Pinoy-Centered Support" desc="We're here to help Filipinos in Canada." />

            <Button
              type="button"
              onClick={onContinue}
              className="w-full h-12 rounded-xl bg-[#061b3a] hover:bg-[#0a2a5a] text-[#f2c766] font-black"
            >
              Continue Application
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-white/85">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-white/90">
      <span className={`h-3 w-3 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}

function Benefit({ icon, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="text-3xl">{icon}</div>
      <div>
        <div className="font-black text-[#061b3a]">{title}</div>
        <div className="text-sm text-slate-600">{desc}</div>
      </div>
    </div>
  );
}

function Row({label, value, bold, accent}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`${bold ? "font-semibold" : ""} ${accent ? "text-emerald-700" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function ProfileForm({ profile, setProfile }) {
  const on = (k) => (e) => setProfile({ ...profile, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-4">
      <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Borrower profile</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full legal name"><Input data-testid="profile-name" value={profile.full_legal_name||""} onChange={on("full_legal_name")}/></Field>
        <Field label="Date of birth"><Input data-testid="profile-dob" type="date" value={profile.date_of_birth||""} onChange={on("date_of_birth")}/></Field>
        <Field label="Mobile"><Input data-testid="profile-mobile" value={profile.mobile||""} onChange={on("mobile")}/></Field>
        <Field label="Address"><Input data-testid="profile-address" value={profile.address||""} onChange={on("address")}/></Field>
        <Field label="Employment status">
          <Select value={profile.employment_status||""} onValueChange={(v)=>setProfile({...profile, employment_status: v})}>
            <SelectTrigger data-testid="profile-employment"><SelectValue placeholder="Select…"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="employed">Employed</SelectItem>
              <SelectItem value="self_employed">Self-employed</SelectItem>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="unemployed">Unemployed</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Employer name"><Input data-testid="profile-employer" value={profile.employer_name||""} onChange={on("employer_name")}/></Field>
        <Field label="Monthly income ($)"><Input data-testid="profile-income" type="number" value={profile.monthly_income||""} onChange={on("monthly_income")}/></Field>
        <Field label="Gov't ID type">
          <Select value={profile.government_id_type||""} onValueChange={(v)=>setProfile({...profile, government_id_type: v})}>
            <SelectTrigger data-testid="profile-id-type"><SelectValue placeholder="Select…"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="driver_license">Driver's License</SelectItem>
              <SelectItem value="national_id">National ID</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Gov't ID number"><Input data-testid="profile-id-number" value={profile.government_id_number||""} onChange={on("government_id_number")}/></Field>
        <Field label="Emergency contact name"><Input data-testid="profile-emc-name" value={profile.emergency_contact_name||""} onChange={on("emergency_contact_name")}/></Field>
        <Field label="Emergency contact phone"><Input data-testid="profile-emc-phone" value={profile.emergency_contact_phone||""} onChange={on("emergency_contact_phone")}/></Field>
      </div>
      <div className="space-y-2 pt-2 border-t border-slate-100">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox data-testid="profile-consent-terms" checked={!!profile.consent_terms} onCheckedChange={(v)=>setProfile({...profile, consent_terms: !!v})}/>
          <span>I agree to Mabuhay Capital Lending Terms of Service, Privacy Policy and the Loan Disclosure.</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox data-testid="profile-consent-credit" checked={!!profile.consent_credit_check} onCheckedChange={(v)=>setProfile({...profile, consent_credit_check: !!v})}/>
          <span>I authorize a credit and identity check.</span>
        </label>
      </div>
    </CardContent></Card>
  );
}

function Field({label, children}) {
  return <div><Label className="mb-1 block">{label}</Label>{children}</div>;
}

function EkycPanel({ ekyc, startEkyc, submitEkyc, loading }) {
  const [idType, setIdType] = useState("passport");
  const [idNumber, setIdNumber] = useState("");
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Identity verification (eKYC)</h2>
          <p className="text-slate-600 text-sm">Provider-agnostic workflow. We mock provider responses for the demo.</p>
        </div>
        {ekyc && <StatusBadge status={ekyc.status}/>}
      </div>
      {!ekyc ? (
        <Button onClick={startEkyc} disabled={loading} className="bg-blue-900 hover:bg-blue-800" data-testid="ekyc-start-btn">{loading ? "Starting…" : "Start eKYC"}</Button>
      ) : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Row label="Provider ref" value={ekyc.provider_ref_id}/>
            <Row label="ID verification" value={ekyc.id_verification}/>
            <Row label="Liveness" value={ekyc.liveness_verification}/>
            <Row label="AML screening" value={ekyc.aml_screening}/>
            <Row label="Address verification" value={ekyc.address_verification}/>
          </div>
          {ekyc.status !== "verified" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="ID document type">
                <Select value={idType} onValueChange={setIdType}>
                  <SelectTrigger data-testid="ekyc-idtype"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="driver_license">Driver's License</SelectItem>
                    <SelectItem value="national_id">National ID</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="ID number">
                <Input data-testid="ekyc-idnumber" value={idNumber} onChange={(e)=>setIdNumber(e.target.value)}/>
              </Field>
              <div className="sm:col-span-2">
                <Button disabled={loading || !idNumber} onClick={() => submitEkyc(idType, idNumber)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ekyc-submit-btn">
                  {loading ? "Verifying…" : "Verify identity"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </CardContent></Card>
  );
}

function DocsPanel({ product, docs, uploadDoc }) {
  const required = product.required_documents || [];
  const byType = Object.fromEntries(docs.map((d) => [d.doc_type, d]));
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Required documents</h2>
        <p className="text-slate-600 text-sm">Upload PDF, PNG, or JPG (max 5MB each).</p>
      </div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {required.map((rt) => {
          const d = byType[rt];
          return (
            <li key={rt} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`doc-row-${rt.replaceAll(' ', '-').toLowerCase()}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileCheck className="h-5 w-5 text-blue-900"/>
                <div>
                  <div className="font-medium text-slate-900">{rt}</div>
                  <div className="text-xs text-slate-500">{d ? d.filename : "Not uploaded"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d && <StatusBadge status={d.status}/>}
                <label className="cursor-pointer">
                  <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid={`doc-upload-${rt.replaceAll(' ', '-').toLowerCase()}`} onChange={(e) => e.target.files?.[0] && uploadDoc(rt, e.target.files[0])}/>
                  <span className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50">
                    {d ? <><RefreshCw className="h-4 w-4"/> Replace</> : <><Upload className="h-4 w-4"/> Upload</>}
                  </span>
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </CardContent></Card>
  );
}

function BankPanel({ bank, setBank, saveBank, profileName }) {
  const on = (k) => (e) => setBank({ ...bank, [k]: e.target.value });
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Bank nomination</h2>
        <p className="text-slate-600 text-sm">Where should we disburse your funds? Account holder should match your legal name: <span className="font-medium text-slate-900">{profileName || "—"}</span></p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Bank name"><Input data-testid="bank-name" value={bank.bank_name||""} onChange={on("bank_name")}/></Field>
        <Field label="Account holder name"><Input data-testid="bank-holder" value={bank.account_holder_name||""} onChange={on("account_holder_name")}/></Field>
        <Field label="Account number"><Input data-testid="bank-account" value={bank.account_number||""} onChange={on("account_number")}/></Field>
        <Field label="Account type">
          <Select value={bank.account_type||"checking"} onValueChange={(v)=>setBank({...bank, account_type: v})}>
            <SelectTrigger data-testid="bank-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="checking">Checking</SelectItem>
              <SelectItem value="savings">Savings</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Transit number"><Input data-testid="bank-transit" value={bank.transit_number||""} onChange={on("transit_number")}/></Field>
        <Field label="Institution number"><Input data-testid="bank-institution" value={bank.institution_number||""} onChange={on("institution_number")}/></Field>
        <Field label="Routing number"><Input data-testid="bank-routing" value={bank.routing_number||""} onChange={on("routing_number")}/></Field>
      </div>
      <Button onClick={saveBank} className="bg-blue-900 hover:bg-blue-800" data-testid="bank-save-btn">Save bank details</Button>
      {bank.name_match === false && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="bank-name-mismatch">⚠ Account holder name doesn't exactly match your legal name. This will be flagged for reviewer.</div>
      )}
    </CardContent></Card>
  );
}

function SubmitPanel({ app, product, calc, docs, ekyc, bank, profile, onSubmit, loading }) {
  const required = product?.required_documents || [];
  const uploaded = new Set(docs.map((d) => d.doc_type));
  const missing = required.filter((r) => !uploaded.has(r));
  const ekycOk = !product?.ekyc_required || ekyc?.status === "verified";
  const bankOk = !product?.bank_nomination_required || (bank && bank.bank_name);
  const profileOk = !!(profile?.full_legal_name && profile?.date_of_birth && profile?.address && profile?.consent_terms);
  const canSubmit = ekycOk && bankOk && profileOk && missing.length === 0;

  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-4">
      <h2 className="font-semibold text-slate-900 text-lg" style={{fontFamily:'Outfit'}}>Review & submit</h2>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Check label="Profile complete" ok={profileOk}/>
        <Check label={`eKYC ${product?.ekyc_required ? "required" : "optional"}`} ok={ekycOk}/>
        <Check label={`Documents (${required.length - missing.length}/${required.length})`} ok={missing.length === 0}/>
        <Check label="Bank nomination" ok={bankOk}/>
      </div>
      {calc && (
        <div className="rounded-lg border border-slate-200 p-4 text-sm space-y-1">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Final terms (frozen at submit)</div>
          <Row label="Principal" value={`$${calc.principal.toFixed(2)}`}/>
          <Row label="Total payable" value={`$${calc.total_payable.toFixed(2)}`} bold/>
          <Row label="Effective APR" value={`${calc.effective_apr}%`} accent/>
          <Row label="Schedule" value={`${calc.n_payments} × $${calc.per_payment.toFixed(2)} (${calc.repayment_frequency})`}/>
        </div>
      )}
      <Button disabled={!canSubmit || loading} onClick={onSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-application-btn">
        {loading ? "Submitting…" : "Submit application"}
      </Button>
      {!canSubmit && <p className="text-xs text-amber-700">Complete all sections to enable submission.</p>}
    </CardContent></Card>
  );
}

function Check({label, ok}) {
  return (
    <div className={`rounded-md border px-3 py-2 flex items-center gap-2 ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
      <span className="h-2 w-2 rounded-full" style={{background: ok ? "#10B981" : "#F59E0B"}}/>
      <span>{label}</span>
    </div>
  );
}
